import {
  BlockLibraryProvider,
  Button,
  CanvasBlockContent,
  FORM_BUILDER_DEFAULT_OPTIONS,
  FM_BUILDER_CANVAS_WIDTH,
  FM_BUILDER_VIEWPORT_WIDTH,
  FormBuilderCanvas,
  FormBuilderConditionModal,
  FormBuilderLibraryPanel,
  FormBuilderRightPanel,
  FormBuilderToolbar,
  Modal,
  useToast,
  type BuilderBlockSource,
  type BuilderLibraryTab,
  type BuilderViewport,
  type CanvasBlock,
  type DragInfo,
  type FormBuilderCanvasSize,
  type FormListItem,
  type FormListStatus,
} from '@elva/ui';
import { useWorkspaceLabel } from '@elva/platform';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePageTopBar } from '@/app/layouts/pageTopBar';
import { keys } from '@/api/queryKeys';
import { extractApiErrorMessage } from '@/api/errors';
import { ElvaPageLoader } from '@/components/ElvaPageLoader';
import {
  buildFormTemplatePayload,
  saveFormTemplate,
  syncFormBlocks,
} from '../adapters/formsAdapters';
import { catalogToLibraryBlocks } from '../adapters/catalogLibrary';
import { formsApi } from '../api/formsApi';
import { useBlockCatalog } from '../hooks/useBlockCatalog';
import {
  useCreateFormTemplateMutation,
  useFormTemplate,
  type FormTemplateQueryData,
} from '../hooks/useFormTemplate';
import { FormSettingsDialog } from '../components/FormSettingsDialog';
import { SendFormDialog } from '../components/SendFormDialog';
import { formatPublishValidationDescription } from '../utils/publishValidation';

export function FormBuilderPage() {
  const workspaceLabel = useWorkspaceLabel();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEditMode = editId != null && editId !== '';

  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [settingsOpen, setSettingsOpen] = useState(!isEditMode);
  const [editingName, setEditingName] = useState(false);
  const [viewport, setViewport] = useState<BuilderViewport>('desktop');
  const [canvasSize, setCanvasSize] = useState<FormBuilderCanvasSize>('regular');
  const [showGrid, setShowGrid] = useState(true);
  const [activeTab, setActiveTab] = useState<BuilderLibraryTab>('patient');
  const [search, setSearch] = useState('');
  const [blocks, setBlocks] = useState<CanvasBlock[]>([]);
  const [selectedUid, setSelectedUid] = useState<string | null>(null);
  const [dragInfo, setDragInfo] = useState<DragInfo>(null);
  const [dropIndex, setDropIndex] = useState<number | null>(null);
  const [conditionFor, setConditionFor] = useState<CanvasBlock | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [testSendOpen, setTestSendOpen] = useState(false);
  const [hydratedEditId, setHydratedEditId] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [status, setStatus] = useState<FormListStatus>('draft');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const {
    data: catalogData,
    isLoading: catalogLoading,
    isError: catalogError,
  } = useBlockCatalog();
  const library = useMemo(
    () => (catalogData ? catalogToLibraryBlocks(catalogData) : []),
    [catalogData],
  );
  const { data: templateData, isLoading: templateLoading, isError: templateError } = useFormTemplate(editId);
  const createTemplate = useCreateFormTemplateMutation();
  const isTemplateHydrating = isEditMode && (templateLoading || hydratedEditId !== editId);
  const isBlockingLoad = isTemplateHydrating || isSaving || catalogLoading;

  useEffect(() => {
    if (!editId || !templateData) return;
    if (hydratedEditId === editId) return;

    const nextBlocks = templateData.blocks;

    setFormName(templateData.template.name);
    setFormDescription(templateData.template.description ?? '');
    setBlocks(nextBlocks);
    setSelectedUid(null);
    setStatus(templateData.template.status === 'active' ? 'published' : templateData.template.status);
    if (templateData.template.updated_at) {
      setLastSavedAt(new Date(templateData.template.updated_at));
    }
    setHydratedEditId(editId);
  }, [editId, hydratedEditId, templateData]);

  useEffect(() => {
    if (editId) return;
    setFormName('');
    setFormDescription('');
    setSettingsOpen(true);
    setBlocks([]);
    setSelectedUid(null);
    setStatus('draft');
    setLastSavedAt(null);
    setHydratedEditId(null);
  }, [editId]);

  if (templateError) console.error('Failed to load form template');

  const selectedBlock = blocks.find((b) => b.uid === selectedUid) ?? null;
  const isPublished = status === 'published';
  const canPersist = blocks.length > 0;

  const persistTemplate = useCallback(async (): Promise<string | null> => {
    if (isEditMode && editId) {
      const payload = isPublished
        ? { name: formName }
        : buildFormTemplatePayload(formName, status === 'archived' ? 'archived' : 'draft');
      const result = await saveFormTemplate(
        editId,
        payload,
        blocks,
        templateData?.conditions ?? [],
      );
      setBlocks(result.blocks);
      setHydratedEditId(result.formId);
      queryClient.setQueryData<FormTemplateQueryData>(keys.forms.template(result.formId), (prev) =>
        prev ? { ...prev, blocks: result.blocks } : prev,
      );
      setLastSavedAt(new Date());
      return result.formId;
    }

    const created = await createTemplate.mutateAsync({ name: formName });
    if (!created.id) return null;
    const synced = await syncFormBlocks(created.id, blocks);
    setBlocks(synced);
    queryClient.setQueryData<FormTemplateQueryData>(keys.forms.template(created.id), {
      template: { id: created.id, name: formName, status: 'draft' },
      blocks: synced,
      conditions: [],
      triggerRules: [],
    });
    void navigate(`/forms/create?edit=${created.id}`, { replace: true });
    setLastSavedAt(new Date());
    return created.id;
  }, [
    blocks,
    createTemplate,
    editId,
    formName,
    isEditMode,
    isPublished,
    navigate,
    status,
    templateData?.conditions,
    queryClient,
  ]);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await persistTemplate();
      toast({
        title: isPublished ? 'Changes saved' : 'Draft saved',
        variant: 'success',
      });
    } catch (error) {
      toast({
        title: 'Save failed',
        description: extractApiErrorMessage(error),
        variant: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  }, [isPublished, persistTemplate, toast]);

  const handlePublish = useCallback(async () => {
    setIsPublishing(true);
    try {
      const id = await persistTemplate();
      if (!id) {
        toast({
          title: 'Publish failed',
          description: 'Could not save the form before publishing.',
          variant: 'error',
        });
        return;
      }

      const validation = await formsApi.validateTemplate(id);
      if (!validation.valid) {
        toast({
          title: 'Cannot publish yet',
          description: formatPublishValidationDescription(validation.errors),
          variant: 'error',
        });
        return;
      }

      await formsApi.updateTemplate(id, { status: 'active' });
      setStatus('published');
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(id) });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
      toast({ title: 'Form published', variant: 'success' });
    } catch (error) {
      toast({ title: 'Publish failed', description: extractApiErrorMessage(error), variant: 'error' });
    } finally {
      setIsPublishing(false);
    }
  }, [persistTemplate, queryClient, toast]);

  const handleUnpublish = useCallback(async () => {
    if (!editId) return;
    setIsPublishing(true);
    try {
      await formsApi.updateTemplate(editId, { status: 'draft' });
      setStatus('draft');
      setLastSavedAt(new Date());
      void queryClient.invalidateQueries({ queryKey: keys.forms.templates });
      void queryClient.invalidateQueries({ queryKey: keys.forms.template(editId) });
      void queryClient.invalidateQueries({ queryKey: keys.formsStats });
      toast({ title: 'Form unpublished', variant: 'success' });
    } catch (error) {
      toast({ title: 'Unpublish failed', description: extractApiErrorMessage(error), variant: 'error' });
    } finally {
      setIsPublishing(false);
    }
  }, [editId, queryClient, toast]);

  usePageTopBar({
    breadcrumb: [workspaceLabel, 'Forms', formName],
  });

  const insertBlock = useCallback(
    (atIndex: number, source: BuilderBlockSource, typeId: string) => {
      const def = library.find((b) => b.id === typeId);
      if (!def) return;
      const defaultOptions = FORM_BUILDER_DEFAULT_OPTIONS[typeId];
      const newBlock: CanvasBlock = {
        uid: crypto.randomUUID(),
        source,
        typeId,
        title: def.label,
        description: def.desc,
        ...(source === 'smart'
          ? { overrides: {} }
          : {
              config: {
                label: def.label,
                required: false,
                ...(defaultOptions ? { options: [...defaultOptions] } : {}),
              },
            }),
      };
      setBlocks((prev) => {
        const next = [...prev];
        next.splice(atIndex, 0, newBlock);
        return next;
      });
      setSelectedUid(newBlock.uid);
    },
    [library],
  );

  const removeBlock = useCallback((uid: string) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.uid === uid);
      const next = prev.filter((b) => b.uid !== uid);
      setSelectedUid((current) => {
        if (current !== uid) return current;
        return next[idx - 1]?.uid ?? next[idx]?.uid ?? null;
      });
      return next;
    });
  }, []);

  const moveBlock = useCallback((uid: string, dir: -1 | 1) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.uid === uid);
      const newIdx = idx + dir;
      if (idx < 0 || newIdx < 0 || newIdx >= prev.length) return prev;
      const next = [...prev];
      const current = next[idx];
      const target = next[newIdx];
      if (!current || !target) return prev;
      next[idx] = target;
      next[newIdx] = current;
      return next;
    });
  }, []);

  const duplicateBlock = useCallback((uid: string) => {
    setBlocks((prev) => {
      const idx = prev.findIndex((b) => b.uid === uid);
      if (idx < 0) return prev;
      const current = prev[idx];
      if (!current) return prev;
      const copy = structuredClone(current);
      copy.uid = crypto.randomUUID();
      const next = [...prev];
      next.splice(idx + 1, 0, copy);
      setSelectedUid(copy.uid);
      return next;
    });
  }, []);

  const updateBlock = useCallback((uid: string, patch: Partial<CanvasBlock>) => {
    setBlocks((prev) => prev.map((block) => (block.uid === uid ? { ...block, ...patch } : block)));
  }, []);

  const moveBlockToIndex = useCallback((uid: string, toIndex: number) => {
    setBlocks((prev) => {
      const fromIndex = prev.findIndex((b) => b.uid === uid);
      if (fromIndex < 0 || fromIndex === toIndex || fromIndex === toIndex - 1) return prev;
      const next = [...prev];
      const [moved] = next.splice(fromIndex, 1);
      if (!moved) return prev;
      const adjusted = fromIndex < toIndex ? toIndex - 1 : toIndex;
      next.splice(adjusted, 0, moved);
      return next;
    });
  }, []);

  const handleLibraryDragStart = (e: React.DragEvent, source: BuilderBlockSource, typeId: string) => {
    setDragInfo({ type: 'library', source, typeId });
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleBlockDragStart = (e: React.DragEvent, uid: string) => {
    setDragInfo({ type: 'reorder', uid });
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragEnd = () => {
    setDragInfo(null);
    setDropIndex(null);
  };

  const handleDropZoneOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragInfo?.type === 'library' ? 'copy' : 'move';
    setDropIndex(index);
  };

  const handleDropZoneDrop = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (dragInfo?.type === 'library') insertBlock(index, dragInfo.source, dragInfo.typeId);
    else if (dragInfo?.type === 'reorder') moveBlockToIndex(dragInfo.uid, index);
    setDragInfo(null);
    setDropIndex(null);
  };

  if (isBlockingLoad) {
    return (
      <div className="fm-root v6-root flex min-h-0 flex-1 flex-col overflow-hidden">
        <ElvaPageLoader />
      </div>
    );
  }

  // D41 degradation rule: a failed catalog fetch surfaces an error — never a
  // fallback to a stale local library (that is how the drift hid for so long).
  if (catalogError) {
    return (
      <div className="fm-root v6-root flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-hidden p-8 text-center">
        <p className="text-[15px] font-semibold text-[var(--ink-0)]">Couldn’t load the block library</p>
        <p className="max-w-md text-[13px] text-[var(--ink-3)]">
          The form builder needs the block catalog from the server and it didn’t load. Refresh to try again.
        </p>
        <Button variant="secondary" size="sm" onClick={() => { void navigate(0); }}>
          Refresh
        </Button>
      </div>
    );
  }

  const handleSettingsClose = () => {
    setSettingsOpen(false);
    if (!editId) {
      void navigate('/forms/templates');
    }
  };

  return (
    <BlockLibraryProvider blocks={library}>
      <div className="fm-root v6-root flex min-h-0 flex-1 flex-col overflow-hidden">
      <FormBuilderToolbar
        formName={formName}
        editingName={editingName}
        onEditingNameChange={setEditingName}
        onFormNameChange={setFormName}
        viewport={viewport}
        onViewportChange={setViewport}
        blockCount={blocks.length}
        status={status}
        lastSavedAt={lastSavedAt}
        isSaving={isSaving}
        isPublishing={isPublishing}
        canPersist={canPersist}
        onPreview={() => {
          setPreviewOpen(true);
        }}
        onSave={() => {
          void handleSave();
        }}
        onPublish={() => {
          void handlePublish();
        }}
        onUnpublish={() => {
          void handleUnpublish();
        }}
      />

      <div className="flex min-h-0 flex-1 overflow-hidden">
        <FormBuilderLibraryPanel
          activeTab={activeTab}
          onTabChange={setActiveTab}
          search={search}
          onSearchChange={setSearch}
          onClickAdd={(source, typeId) => {
            insertBlock(blocks.length, source, typeId);
          }}
          onDragStart={handleLibraryDragStart}
          onDragEnd={handleDragEnd}
        />

        <FormBuilderCanvas
          blocks={blocks}
          selectedUid={selectedUid}
          onSelect={setSelectedUid}
          viewport={viewport}
          {...(viewport === 'desktop'
            ? { canvasWidth: FM_BUILDER_CANVAS_WIDTH[canvasSize] }
            : {})}
          showGrid={showGrid}
          dragInfo={dragInfo}
          dropIndex={dropIndex}
          onBlockDragStart={handleBlockDragStart}
          onBlockDragEnd={handleDragEnd}
          onDropZoneOver={handleDropZoneOver}
          onDropZoneLeave={() => {
            setDropIndex(null);
          }}
          onDropZoneDrop={handleDropZoneDrop}
          onRemove={removeBlock}
          onMove={moveBlock}
          onDuplicate={duplicateBlock}
        />

        <FormBuilderRightPanel
          selectedBlock={selectedBlock}
          formName={formName}
          blockCount={blocks.length}
          updateBlock={updateBlock}
          onAddCondition={setConditionFor}
          onOpenFormSettings={() => {
            setSettingsOpen(true);
          }}
          onRemoveCondition={(uid) => {
            setBlocks((prev) =>
              prev.map((block) => {
                if (block.uid !== uid) return block;
                const { condition: _removed, ...rest } = block;
                return rest;
              }),
            );
          }}
        />
      </div>

      {settingsOpen ? (
        <FormSettingsDialog
          open
          variant={editId ? 'settings' : 'create'}
          formId={editId}
          initialName={formName}
          initialDescription={formDescription}
          onClose={handleSettingsClose}
          onSaved={({ formId, name, description }) => {
            setFormName(name);
            setFormDescription(description);
            setLastSavedAt(new Date());
            setSettingsOpen(false);
            if (!editId) {
              void navigate(`/forms/create?edit=${formId}`, { replace: true });
            }
          }}
        />
      ) : null}

      {testSendOpen && editId ? (
        <SendFormDialog
          form={{
            id: editId,
            name: formName,
            desc: formDescription,
            status,
            trigger: '',
            submitted: 0,
            sent: 0,
            completion: 0,
            blocks: blocks.length,
            lastSent: 'never',
          } satisfies FormListItem}
          onClose={() => {
            setTestSendOpen(false);
          }}
        />
      ) : null}

      {conditionFor ? (
        <FormBuilderConditionModal
          open
          block={conditionFor}
          blocks={blocks}
          onClose={() => {
            setConditionFor(null);
          }}
          onSave={(condition, alsoApplyTo) => {
            const targets = new Set([conditionFor.uid, ...alsoApplyTo]);
            setBlocks((prev) =>
              prev.map((block) => (targets.has(block.uid) ? { ...block, condition: { ...condition } } : block)),
            );
            setConditionFor(null);
          }}
        />
      ) : null}

      <Modal
        open={previewOpen}
        title={formName}
        onClose={() => {
          setPreviewOpen(false);
        }}
        className="flex w-[min(960px,calc(100vw-48px))] max-h-[min(900px,calc(100vh-48px))] flex-col [&>div:nth-child(2)]:max-h-[60vh] [&>div:nth-child(2)]:overflow-y-auto [&>div:nth-child(2)]:bg-[var(--cream-0)] [&>div:nth-child(2)]:p-0"
        footer={
          <div className="flex w-full items-center justify-between gap-4">
            <p className="text-[11.5px] text-[var(--ink-3)]">
              {editId
                ? 'Patient-facing preview — conditional blocks are shown regardless of rules.'
                : 'Save the form to send a test to a patient.'}
            </p>
            <div className="flex items-center gap-2">
              {/* W15 — test send reuses POST /forms/send (via SendFormDialog + W21 channel). */}
              <Button
                variant="secondary"
                size="sm"
                iconLeft="send"
                disabled={!editId}
                onClick={() => {
                  setPreviewOpen(false);
                  setTestSendOpen(true);
                }}
              >
                Send test
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => {
                  setPreviewOpen(false);
                }}
              >
                Close
              </Button>
            </div>
          </div>
        }
      >
        <div className="px-5 py-8">
          <div
            className="mx-auto transition-[width] duration-300 ease-[cubic-bezier(0.16,1,0.3,1)]"
            style={{ width: FM_BUILDER_VIEWPORT_WIDTH[viewport], maxWidth: '100%' }}
          >
            <div className="fm-builder-canvas-sheet">
              {blocks.length === 0 ? (
                <p className="py-16 text-center text-[13px] text-[var(--ink-3)]">
                  Add blocks to the form to see a preview.
                </p>
              ) : (
                <div className="flex flex-col gap-5">
                  {blocks.map((block) => (
                    <div key={block.uid}>
                      <CanvasBlockContent block={block} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </Modal>

      <div className="pointer-events-none fixed bottom-4 right-4 z-20 flex gap-2 opacity-0 transition-opacity hover:opacity-100 focus-within:opacity-100">
        <div className="pointer-events-auto flex items-center gap-2 rounded-lg border border-[var(--cream-2)] bg-[var(--cream-1)] px-3 py-2 text-[11px] shadow-[var(--sh-2)]">
          <label className="flex items-center gap-1.5 text-[var(--ink-3)]">
            <input
              type="checkbox"
              checked={showGrid}
              onChange={(e) => {
                setShowGrid(e.target.checked);
              }}
            />
            Dot grid
          </label>
          <select
            value={canvasSize}
            onChange={(e) => {
              setCanvasSize(e.target.value as FormBuilderCanvasSize);
            }}
            className="rounded border border-[var(--cream-2)] bg-[var(--cream-0)] px-2 py-0.5 text-[var(--ink-2)]"
          >
            <option value="narrow">Narrow</option>
            <option value="regular">Regular</option>
            <option value="wide">Wide</option>
          </select>
        </div>
      </div>
      </div>
    </BlockLibraryProvider>
  );
}
